import { useLazyGetProductQuery } from '.codegen/generatedApi'
import Image from '@feature/Image'
import Link from '@feature/Link'
import { Box, BoxTypeMap, Fade, Stack, Typography } from '@mui/material'
import {
  OverridableComponent,
  OverrideProps
} from '@mui/material/OverridableComponent'
import { useAppSelector } from 'app/hooks'
import { selectProductBySlug } from 'app/results/resultsSlice'
import { getSelectProductUri, getSelectTermsById } from 'app/selectors'
import { ParsedProduct } from 'helpers'
import { useSelector } from 'react-redux'

type ProductCustomProps = {
  entity: ParsedProduct
  hideTitle?: boolean
  hideBrand?: boolean
}
type ProductTypeMap<P = {}, D extends React.ElementType = 'div'> = BoxTypeMap<ProductCustomProps & P, D>

export type ProductProps<D extends React.ElementType = BoxTypeMap['defaultComponent'], P = {}> = OverrideProps<ProductTypeMap<P, D>, D>

const Product: OverridableComponent<ProductTypeMap> = (props: ProductProps) => {
  const {
    entity: { slug },
    hideTitle,
    hideBrand,
    ...boxProps
  } = props
  const product = useAppSelector(selectProductBySlug(slug))
  const [brandTerm] = useAppSelector(getSelectTermsById('BRAND', product ? product.brands.map((t) => t.id) : []))

  const { id, title, brand, featuredImage, sticker } = product || {}
  const productUri = useSelector(getSelectProductUri(slug))
  // Pre load the full product on hover
  const loadOnHover = false
  const [triggerLoad] = useLazyGetProductQuery()

  return (
    <Box key={id} {...boxProps}>
      <Link to={productUri} color="inherit" underline="none" onMouseEnter={() => loadOnHover && triggerLoad({ slug })}>
        <Fade in>
          <Stack component={Box} height={'100%'} textAlign="center" spacing={0.3}>
            <Image imageData={featuredImage} stickerType={product?.sticker} stickerImage={product?.stickerImage} contain withBorder fillPercentage={90} size={'LARGE'} mb={0.6} />
            {!hideTitle && <Typography variant="large" color={hideBrand ? 'grey.500' : 'initial'} sx={{ wordBreak: 'break-word'}}>{`${title}`}</Typography>}
            {!hideBrand && <Typography color="grey.700">{brandTerm?.name || brand}</Typography>}
          </Stack>
        </Fade>
      </Link>
    </Box>
  )
}

export default Product
